import {getTestProps} from '@benchling/js-test-utils';
import {Box, createStyles, Flex, Group, Paper, Skeleton, Text} from '@mantine/core';
import React, {useCallback, useMemo} from 'react';
import {LayoutItem, Responsive, ResponsiveLayouts} from 'react-grid-layout';

import useResizeObserver from 'ui-library/hooks/useResizeObserver';

import {
  DEFAULT_HOMEPAGE_CARD_LAYOUTS,
  getVisibleHomepageCards,
  HOMEPAGE_CARD_DEFINITIONS,
  HomepageCardDefinition,
  HomepageCardId,
} from '../HomepageCardDefinitions';
import {
  filterHomepageCardLayouts,
  HOMEPAGE_CARD_LAYOUT_BREAKPOINTS,
  HOMEPAGE_CARD_LAYOUT_COLUMN_SPACING_PX,
  HOMEPAGE_CARD_LAYOUT_COLS,
  HOMEPAGE_CARD_LAYOUT_EDIT_ROW_SPACING_PX,
  HOMEPAGE_CARD_LAYOUT_ROW_HEIGHT_PX,
  HOMEPAGE_CARD_LAYOUT_VIEW_CARD_BOTTOM_SPACING_PX,
  HomepageCardLayouts,
  HomepageCardLayoutBreakpoint,
  getHomepageCardLayoutViewSections,
  mergeHomepageCardLayoutChanges,
} from './HomepageCardLayout';

const useEditStyles = createStyles((theme) => ({
  grid: {
    '&.react-grid-layout': {
      position: 'relative',
    },
    '& .react-grid-item': {
      transition: 'left 200ms ease, top 200ms ease',
      border: `1px dashed transparent`,
      borderRadius: theme.radius.lg,
      padding: theme.spacing.xs,
      cursor: 'grab',
      '&:hover': {
        borderColor: theme.colors.blue[6],
      },
    },
    '& .react-grid-item.cssTransforms': {
      transition: 'transform 200ms ease',
    },
    '& .react-grid-item.react-draggable-dragging': {
      transition: 'none',
      zIndex: 3,
      willChange: 'transform',
    },
    '& .react-grid-item.react-grid-placeholder': {
      backgroundColor: theme.colors.blue[6],
      opacity: 0.16,
      transitionDuration: '100ms',
      zIndex: 2,
      userSelect: 'none',
      borderRadius: theme.radius.lg,
    },
  },
}));

const useViewStyles = createStyles(() => ({
  viewColumns: {
    alignItems: 'flex-start',
    columnGap: HOMEPAGE_CARD_LAYOUT_COLUMN_SPACING_PX,
    display: 'grid',
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
  },
}));

const useProxyCardStyles = createStyles((theme) => ({
  proxyCard: {
    padding: theme.spacing.md,
  },
  proxyTitle: {
    fontWeight: theme.other.fontWeights.semibold,
  },
}));

export interface HomepageCardGridProps {
  editingLayout: boolean;
  layouts: HomepageCardLayouts;
  onLayoutsChange: (layouts: HomepageCardLayouts) => void;
}

export default function HomepageCardGrid({
  editingLayout,
  layouts,
  onLayoutsChange,
}: HomepageCardGridProps): JSX.Element {
  const {ref: dashboardRef, width} = useResizeObserver<HTMLDivElement>();
  const gridWidth = width ?? HOMEPAGE_CARD_LAYOUT_BREAKPOINTS.wide;
  const breakpoint: HomepageCardLayoutBreakpoint =
    gridWidth >= HOMEPAGE_CARD_LAYOUT_BREAKPOINTS.wide ? 'wide' : 'narrow';

  const visibleCards = getVisibleHomepageCards();
  const visibleCardIds = useMemo(() => new Set(visibleCards.map(({id}) => id)), [visibleCards]);

  return (
    <Box
      ref={dashboardRef}
      mt={editingLayout ? HOMEPAGE_CARD_LAYOUT_EDIT_ROW_SPACING_PX : 0}
      {...getTestProps({component: 'HomepageCardGrid', element: 'root'})}
    >
      {editingLayout ? (
        <HomepageCardGridEditView
          layouts={layouts}
          onLayoutsChange={onLayoutsChange}
          visibleCardIds={visibleCardIds}
          visibleCards={visibleCards}
          width={gridWidth}
        />
      ) : (
        <HomepageCardGridView
          layouts={layouts}
          visibleCardIds={visibleCardIds}
          visibleCards={visibleCards}
          breakpoint={breakpoint}
        />
      )}
    </Box>
  );
}

function HomepageCardGridEditView({
  layouts,
  onLayoutsChange,
  visibleCardIds,
  visibleCards,
  width,
}: {
  layouts: HomepageCardLayouts;
  onLayoutsChange: (layouts: HomepageCardLayouts) => void;
  visibleCardIds: ReadonlySet<HomepageCardId>;
  visibleCards: Array<HomepageCardDefinition>;
  width: number;
}): JSX.Element {
  const {classes} = useEditStyles();
  const editLayouts = useMemo(
    () =>
      filterHomepageCardLayouts(
        layouts,
        visibleCardIds,
        HOMEPAGE_CARD_DEFINITIONS,
        DEFAULT_HOMEPAGE_CARD_LAYOUTS
      ),
    [layouts, visibleCardIds]
  );

  const handleLayoutChange = useCallback(
    (_layout: Array<LayoutItem>, nextLayouts: ResponsiveLayouts<string>): void => {
      onLayoutsChange(
        mergeHomepageCardLayoutChanges({
          currentLayouts: layouts,
          changedLayouts: nextLayouts,
          changedCardIds: visibleCardIds,
          cardLayoutDefinitions: HOMEPAGE_CARD_DEFINITIONS,
          defaultLayouts: DEFAULT_HOMEPAGE_CARD_LAYOUTS,
        })
      );
    },
    [layouts, onLayoutsChange, visibleCardIds]
  );

  return (
    <Responsive
      className={classes.grid}
      width={width}
      breakpoints={HOMEPAGE_CARD_LAYOUT_BREAKPOINTS}
      cols={HOMEPAGE_CARD_LAYOUT_COLS}
      layouts={editLayouts}
      rowHeight={HOMEPAGE_CARD_LAYOUT_ROW_HEIGHT_PX}
      margin={[HOMEPAGE_CARD_LAYOUT_COLUMN_SPACING_PX, 0]}
      containerPadding={[0, 0]}
      dragConfig={{enabled: true}}
      resizeConfig={{enabled: false}}
      onLayoutChange={handleLayoutChange}
    >
      {visibleCards.map((card) => (
        <div
          key={card.id}
          data-amp-safe={false}
          {...getTestProps({component: 'HomepageCardGrid', element: 'tile', key: card.id})}
        >
          <HomepageCardGridProxyCard card={card} />
        </div>
      ))}
    </Responsive>
  );
}

function HomepageCardGridView({
  breakpoint,
  layouts,
  visibleCardIds,
  visibleCards,
}: {
  breakpoint: HomepageCardLayoutBreakpoint;
  layouts: HomepageCardLayouts;
  visibleCardIds: ReadonlySet<HomepageCardId>;
  visibleCards: Array<HomepageCardDefinition>;
}): JSX.Element {
  const {classes} = useViewStyles();
  const cardsById = useMemo(
    () =>
      Object.fromEntries(visibleCards.map((card) => [card.id, card])) as Partial<
        Record<HomepageCardId, HomepageCardDefinition>
      >,
    [visibleCards]
  );
  const sections = useMemo(
    () =>
      getHomepageCardLayoutViewSections({
        layouts,
        visibleCardIds,
        breakpoint,
        cardLayoutDefinitions: HOMEPAGE_CARD_DEFINITIONS,
        defaultLayouts: DEFAULT_HOMEPAGE_CARD_LAYOUTS,
      }),
    [breakpoint, layouts, visibleCardIds]
  );
  const renderCard = (cardId: HomepageCardId): JSX.Element => {
    const {Card} = getCardById(cardsById, cardId);
    return <Card key={cardId} />;
  };

  return (
    <Flex direction="column" gap={HOMEPAGE_CARD_LAYOUT_VIEW_CARD_BOTTOM_SPACING_PX}>
      {sections.map((section, index) => {
        if (section.sectionType === 'fullWidth') {
          return renderCard(section.cardId);
        }

        return (
          <div className={classes.viewColumns} key={`columns-${index}`}>
            <Flex direction="column" gap={HOMEPAGE_CARD_LAYOUT_VIEW_CARD_BOTTOM_SPACING_PX} miw={0}>
              {section.leftCardIds.map((cardId) => renderCard(cardId))}
            </Flex>
            <Flex direction="column" gap={HOMEPAGE_CARD_LAYOUT_VIEW_CARD_BOTTOM_SPACING_PX} miw={0}>
              {section.rightCardIds.map((cardId) => renderCard(cardId))}
            </Flex>
          </div>
        );
      })}
    </Flex>
  );
}

/**
 * View sections are built from the same visible-card set used for rendering; a miss means those
 * inputs have diverged and the layout would otherwise silently drop a card.
 */
function getCardById(
  cardsById: Partial<Record<HomepageCardId, HomepageCardDefinition>>,
  cardId: HomepageCardId
): HomepageCardDefinition {
  const card = cardsById[cardId];
  if (card == null) {
    throw new Error(`Expected visible homepage card for id: ${cardId}`);
  }
  return card;
}

function HomepageCardGridSkeleton({mb}: {mb?: string}): JSX.Element {
  const width = useMemo(() => Math.random() * 60 + 20, []);
  return (
    <Group position="apart" mb={mb}>
      <Skeleton height={10} width={`${width}%`} animate={false} />
      <Skeleton height={10} width="15%" animate={false} />
    </Group>
  );
}

function HomepageCardGridProxyCard({
  card: {id, Placeholder, title},
}: {
  card: HomepageCardDefinition;
}): JSX.Element {
  const {classes} = useProxyCardStyles();

  return (
    <div
      role="group"
      aria-label={title}
      {...getTestProps({component: 'HomepageCardGrid', element: 'proxyTile', key: id})}
      data-amp-safe={false}
    >
      <Text className={classes.proxyTitle} fz="xl">
        {title}
      </Text>
      {Placeholder != null ? (
        <Placeholder proxyCardClassName={classes.proxyCard} />
      ) : (
        <Paper radius="lg" withBorder={true} className={classes.proxyCard}>
          <HomepageCardGridSkeleton mb="md" />
          <HomepageCardGridSkeleton mb="md" />
          <HomepageCardGridSkeleton />
        </Paper>
      )}
    </div>
  );
}
